import { useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { uploadApi } from '../services/api'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/Badge'
import { Upload, File, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

interface FileWithStatus {
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  result?: {
    candidate?: {
      name: string
      email?: string
      phone?: string
      years_of_experience?: number
    }
    errors?: string[]
    error?: string
  }
}

export default function MassUploadPage() {
  const { recruiter } = useAuth()
  const [files, setFiles] = useState<FileWithStatus[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<{
    success: number
    failed: number
    total: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase()
      return ext && ['pdf', 'doc', 'docx'].includes(ext)
    })

    if (droppedFiles.length > 0) {
      const newFiles: FileWithStatus[] = droppedFiles.map(file => ({
        file,
        status: 'pending' as const
      }))
      setFiles(prev => [...prev, ...newFiles])
      setError(null)
    }
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files).filter(file => {
        const ext = file.name.split('.').pop()?.toLowerCase()
        return ext && ['pdf', 'doc', 'docx'].includes(ext)
      })

      if (selectedFiles.length > 0) {
        const newFiles: FileWithStatus[] = selectedFiles.map(file => ({
          file,
          status: 'pending' as const
        }))
        setFiles(prev => [...prev, ...newFiles])
        setError(null)
      }
    }
  }, [])

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || !recruiter) {
      setError('אנא בחר קבצים להעלאה')
      return
    }

    setIsUploading(true)
    setError(null)
    setUploadResult(null)

    // Update all files to uploading status
    setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const })))

    try {
      const fileList = files.map(f => f.file)
      const result = await uploadApi.uploadCandidates(fileList)

      // Update files with results
      setFiles(prev => prev.map((f, index) => {
        const fileResult = result.results[index]
        if (fileResult) {
          return {
            ...f,
            status: fileResult.status === 'success' ? 'success' as const : 
                   fileResult.status === 'failed' ? 'error' as const : 'error' as const,
            result: {
              candidate: fileResult.candidate,
              errors: fileResult.errors,
              error: fileResult.error
            }
          }
        }
        return f
      }))

      setUploadResult({
        success: result.summary.success,
        failed: result.summary.failed,
        total: result.summary.total
      })
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בהעלאת הקבצים')
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' as const })))
    } finally {
      setIsUploading(false)
    }
  }, [files, recruiter])

  const clearAll = useCallback(() => {
    setFiles([])
    setUploadResult(null)
    setError(null)
  }, [])

  return (
    <div className="bg-background p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">העלאת קורות חיים</h1>
          <p className="text-muted-foreground mt-1">
            העלה קבצי PDF או Word להעלאת מועמדים למערכת
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Summary */}
      {uploadResult && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <p>
                הועלו {uploadResult.success} מתוך {uploadResult.total} קבצים בהצלחה
                {uploadResult.failed > 0 && `, ${uploadResult.failed} נכשלו`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>בחר קבצים להעלאה</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drag and Drop Area */}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-primary transition-colors cursor-pointer"
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              גרור קבצים לכאן או לחץ לבחירה
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              PDF, DOC, DOCX (עד 10MB לכל קובץ, מקסימום 50 קבצים)
            </p>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button type="button" variant="outline">
              בחר קבצים
            </Button>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">קבצים נבחרים ({files.length})</h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={isUploading}
                >
                  נקה הכל
                </Button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {files.map((fileWithStatus, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg bg-card"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {fileWithStatus.file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(fileWithStatus.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        {fileWithStatus.result?.candidate && (
                          <div className="mt-1 text-xs">
                            <span className="text-muted-foreground">
                              {fileWithStatus.result.candidate.name}
                              {fileWithStatus.result.candidate.email && ` • ${fileWithStatus.result.candidate.email}`}
                              {fileWithStatus.result.candidate.years_of_experience && 
                                ` • ${fileWithStatus.result.candidate.years_of_experience} שנים ניסיון`}
                            </span>
                          </div>
                        )}
                        {fileWithStatus.result?.error && (
                          <p className="text-xs text-red-600 mt-1">
                            {fileWithStatus.result.error}
                          </p>
                        )}
                        {fileWithStatus.result?.errors && fileWithStatus.result.errors.length > 0 && (
                          <p className="text-xs text-yellow-600 mt-1">
                            {fileWithStatus.result.errors.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {fileWithStatus.status === 'uploading' && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                      {fileWithStatus.status === 'success' && (
                        <Badge variant="default" className="text-xs">
                          הצליח
                        </Badge>
                      )}
                      {fileWithStatus.status === 'error' && (
                        <Badge variant="destructive" className="text-xs">
                          נכשל
                        </Badge>
                      )}
                      {fileWithStatus.status === 'pending' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFile(index)
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && (
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={clearAll}
                disabled={isUploading}
              >
                ביטול
              </Button>
              <Button
                type="button"
                onClick={handleUpload}
                disabled={isUploading || files.length === 0}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    מעלה...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    העלה קבצים
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
